'use client';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@routly/ui';
import { History } from 'lucide-react';
import { useState } from 'react';
import { useBackfill } from '@/hooks/useBackfill';
import { useFleetHealth } from '@/hooks/useFleetHealth';

/** What the engine can answer for. Raw telemetry is kept seven days. */
const MAX_DAYS = 7;

const isoDay = (daysAgo: number) => {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - daysAgo);
  return date.toISOString().slice(0, 10);
};

/**
 * Recomputing a range after changing a calculator.
 *
 * The engine is forward-only: a threshold edited today does not reach
 * yesterday. Without this, an operator changes what a trip is, sees the change
 * apply to trips that have not happened yet, and has no way to make the week
 * they were looking at agree with it.
 *
 * Deliberately one vehicle and one range at a time rather than a fleet-wide
 * recompute. flespi does it automatically and a fleet's history moves under it
 * without anyone asking; the figures a logbook was built on are exactly the
 * ones that should not change quietly.
 */
export function BackfillPanel() {
  const { data } = useFleetHealth();
  const backfill = useBackfill();

  const [vehicleId, setVehicleId] = useState('');
  const [from, setFrom] = useState(isoDay(1));
  const [to, setTo] = useState(isoDay(0));

  const vehicles = data?.freshness ?? [];
  const days = (Date.parse(to) - Date.parse(from)) / 86_400_000;
  const rangeError =
    Number.isNaN(days) || days <= 0
      ? 'The range ends before it begins'
      : days > MAX_DAYS
        ? `Raw telemetry is kept ${MAX_DAYS} days; a wider range would replace existing intervals with nothing`
        : null;

  const run = () => {
    if (!vehicleId || rangeError) return;
    backfill.mutate({
      vehicleId,
      from: new Date(`${from}T00:00:00Z`).toISOString(),
      to: new Date(`${to}T23:59:59Z`).toISOString(),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recompute a range</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          A calculator only applies to messages that arrive after it is saved. Replay a vehicle to
          make an earlier range agree with the rules above — it deletes the intervals it is about to
          recompute, so the range is not counted twice, and leaves anything derived from an event
          alone.
        </p>

        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label htmlFor="backfill-vehicle">Vehicle</Label>
            <Select value={vehicleId} onValueChange={setVehicleId}>
              <SelectTrigger id="backfill-vehicle" className="w-64">
                <SelectValue placeholder="Choose a vehicle" />
              </SelectTrigger>
              <SelectContent>
                {vehicles.map((vehicle) => (
                  <SelectItem key={vehicle.id} value={vehicle.id}>
                    {vehicle.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="backfill-from">From</Label>
            <Input
              id="backfill-from"
              type="date"
              value={from}
              max={to}
              onChange={(event) => setFrom(event.target.value)}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="backfill-to">To</Label>
            <Input
              id="backfill-to"
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
            />
          </div>

          <Button onClick={run} disabled={!vehicleId || rangeError !== null || backfill.isPending}>
            <History className="mr-2 h-4 w-4" />
            {backfill.isPending ? 'Replaying…' : 'Replay'}
          </Button>
        </div>

        {rangeError ? <p className="text-sm text-destructive">{rangeError}</p> : null}

        {backfill.error ? (
          <p className="text-sm text-destructive">
            {backfill.error instanceof Error ? backfill.error.message : 'Replay failed'}
          </p>
        ) : null}

        {backfill.data ? (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge variant="outline">{backfill.data.messagesReplayed} messages replayed</Badge>
            <Badge variant="outline">{backfill.data.intervalsWritten} intervals written</Badge>
            <Badge variant="secondary">{backfill.data.intervalsReplaced} replaced</Badge>
            {backfill.data.messagesReplayed === 0 ? (
              // Not an error, and worth saying plainly: the range is outside
              // what ClickHouse still holds, or the vehicle was silent.
              <span className="text-muted-foreground">
                Nothing to replay — no telemetry stored for that range.
              </span>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
